import { DeviceType } from '@/constants/device';
import { isLikelyVideoUrl } from './validation';

/**
 * Utility functions for download functionality
 */

/**
 * Validates if a URL is downloadable
 */
export const isValidDownloadUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
};

/**
 * Generates a consistent filename for downloads
 */
export const generateDownloadFilename = (
  wallpaperId: string,
  resolution: string,
  deviceType?: DeviceType,
  customName?: string,
  imageUrl?: string
): string => {
  if (customName) return customName;

  const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const deviceSuffix = deviceType ? `_${deviceType}` : '';
  const resolutionSuffix = resolution ? `_${resolution.replace('×', 'x')}` : '';

  // Determine file extension based on URL
  const isVideo = imageUrl ? isLikelyVideoUrl(imageUrl) : false;
  const extension = isVideo ? '.mp4' : '.jpg';

  return `wallpaper_${wallpaperId}${deviceSuffix}${resolutionSuffix}_${timestamp}${extension}`;
};

/**
 * Estimates file size based on dimensions
 */
export const estimateFileSize = (width: number, height: number): string => {
  const pixels = width * height;
  const estimatedBytes = pixels * 3; // Rough estimate for compressed image
  
  if (estimatedBytes > 1024 * 1024) {
    return `${(estimatedBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(estimatedBytes / 1024).toFixed(0)} KB`;
};

/**
 * Gets device-appropriate resolution recommendations
 */
export const getDeviceResolutionRecommendations = (deviceType: DeviceType) => {
  const recommendations = {
    [DeviceType.MOBILE_PHONE]: [
      { width: 1080, height: 1920, name: 'FHD+' },
      { width: 1440, height: 2560, name: 'QHD+' },
      { width: 1080, height: 2340, name: 'FHD+ 19.5:9' }
    ],
    [DeviceType.TABLET]: [
      { width: 1536, height: 2048, name: 'iPad Air' },
      { width: 2048, height: 2732, name: 'iPad Pro 12.9"' },
      { width: 1668, height: 2388, name: 'iPad Pro 11"' }
    ],
    [DeviceType.DESKTOP]: [
      { width: 1920, height: 1080, name: 'Full HD' },
      { width: 2560, height: 1440, name: 'QHD' },
      { width: 3840, height: 2160, name: '4K UHD' }
    ],
    [DeviceType.ULTRAWIDE]: [
      { width: 3440, height: 1440, name: '21:9 QHD' },
      { width: 5120, height: 1440, name: '32:9 QHD' },
      { width: 2560, height: 1080, name: '21:9 FHD' }
    ]
  };

  return recommendations[deviceType] || recommendations[DeviceType.DESKTOP];
};

/**
 * Checks if download is supported in current browser
 */
export const isDownloadSupported = (): boolean => {
  return typeof document !== 'undefined' && 'createElement' in document;
};

/**
 * Creates a download progress tracker
 */
export class DownloadProgressTracker {
  private callbacks: Array<(progress: number) => void> = [];

  onProgress(callback: (progress: number) => void) {
    this.callbacks.push(callback);
  }

  updateProgress(loaded: number, total: number) {
    const progress = total > 0 ? (loaded / total) * 100 : 0;
    this.callbacks.forEach(callback => callback(progress));
  }

  reset() {
    this.callbacks = [];
  }
}